import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {UniqueIdService} from './unique-id.service';
import {UserService} from './user.service';
import * as jwt_decode from 'jwt-decode';
import {AlertService} from './alert.service';
import {MqttBlockingService} from './mqtt-blocking.service';
import {PlannedSiteFilterModel} from '../pages/planner/objects/planned-site-filter.model';
import {SiteDto} from '../pages/planner/objects/site.dto';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, switchMap, timeout} from 'rxjs/operators';
import {MqttGetDeviceDto} from '../interfaces/mqtt/mqtt-get-device.dto';
import {
  GatewayType,
  IDeviceData,
  IImplementedMetersResponse,
  IModuleConfig,
  isReadOnlyModuleType,
  IUpdateIrValuesResponse,
} from '../interfaces/juconnGateway.response';
import {
  CreateInstallationDataRequestModel
} from '../models/create-installation-data-request/create-installation-data-request.model';
import {
  CreateProductSpecificDataRequestModel
} from '../models/create-installation-data-request/create-product-specific-data-request.model';
import {CreateUvsModuleRequestModel} from '../models/create-installation-data-request/create-uvs-module-request.model';
import {
  CreateMeterSensorsImpulseRequestModel
} from '../models/create-installation-data-request/create-meter-sensors-impulse-request.model';
import {IDataGWResponse} from '../interfaces/iotMaxxGateway.response';
import {IInstallationDataResponse} from '../interfaces/installation-data.response';
import {OngoingInstallationDataRequest} from '../models/ongoing-installation-data.request';
import {OngoingUvsModuleRequest} from '../models/ongoing-uvs-module.request';
import {OngoingProductSpecificDataRequest} from '../models/ongoing-product-specific-data.request';
import {OngoingOutsideTemperatureModuleRequest} from '../models/ongoing-outside-temperature-module.request';
import {IRMeterTypeEnum} from '../models/ir-meter-type.enum';
import {
  CreateOutsideTemperatureModuleRequestModel
} from '../models/create-installation-data-request/create-outside-temperature-module-request.model';
import {User} from '../models/user-model';
import {OtherSensorOptionEnum} from '../enums/other-sensor-option-enum';
import {InstallationSubmissionData} from '../models/installation-submission-data';
import {GatewayStyleEnum} from '../models/gateway-style.enum';
import {TemperatureSensorModel} from '../models/temperature-sensor-model';
import {SensorModeType} from '../enums/sensor-mode-type';
import {UvsModuleModel} from '../models/uvs-module.model';
import {MqttGetModuleConfigDto} from '../interfaces/mqtt/mqtt-get-module-config.dto';
import {OtmVerificationResponse} from '../interfaces/otm-verification.response';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  public baseUrl = environment.baseUrl;
  public dataBaseUrl = environment.dataBaseUrl;
  public juconnApiUrl = environment.juconnApiUrl;

  public dataServiceUser = environment.serviceUser;
  public dataServicePwd = environment.servicePW;

  private readonly PROJECT_NAME = 'immoconn';
  private readonly OTM_ADDRESS_OFFSET = 59;

  constructor(
    private http: HttpClient,
    private uniqueIdService: UniqueIdService,
    private userService: UserService,
    private alertService: AlertService,
    private mqttBlockingService: MqttBlockingService,
  ) {
  }

  public loadModuleConfig$(
    gateway: string,
    project?: string,
  ): Observable<MqttGetModuleConfigDto> {
    return this.loadGatewayData$<MqttGetModuleConfigDto[]>(
      gateway,
      'getConfig',
      'loadModuleConfig',
      null,
      project,
    ).pipe(map((result) => result?.[0]));
  }

  public loadDeviceInfo$(
    gateway: string,
    project?: string,
  ): Observable<MqttGetDeviceDto> {
    return this.loadGatewayData$<MqttGetDeviceDto[]>(
      gateway,
      'getDevice',
      'loadGatewayGetDevice',
      null,
      project,
    ).pipe(map((result) => result?.[0]));
  }

  public loadDeviceData$(
    gateway: string,
    project?: string,
  ): Observable<IDeviceData> {
    return this.loadGatewayData$<IDeviceData[]>(
      gateway,
      'getData',
      'loadDeviceData',
      null,
      project,
    ).pipe(
      map((result) => this.normalizeDeviceDataModuleAddresses(result?.[0])),
    );
  }

  public updateIrValues$(
    gateway: string,
    project?: string,
  ): Observable<IUpdateIrValuesResponse> {
    return this.loadGatewayData$<IUpdateIrValuesResponse[]>(
      gateway,
      'updateIrValues',
      'updateIrValues',
      null,
      project,
    ).pipe(map((result) => result?.[0]));
  }

  public getImplementedMeters$(
    gateway: string,
    project?: string,
  ): Observable<IImplementedMetersResponse> {
    return this.loadGatewayData$<IImplementedMetersResponse[]>(
      gateway,
      'getImplementedMeters',
      'getImplementedMeters',
      null,
      project,
    ).pipe(map((result) => result?.[0]));
  }

  public getGatewayBySerialNumber$(
    serialNumber: string,
    project?: string,
  ): Observable<IInstallationDataResponse> {
    if (!project) project = this.PROJECT_NAME;
    const url = `${this.dataBaseUrl}/api/v3/${project}/installation/${serialNumber}`;
    return from(this.prepareAuthorizationBearerToken()).pipe(
      switchMap((authToken) =>
        this.http.get<IInstallationDataResponse>(url, {
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            Authorization: authToken,
          },
        }),
      ),
      catchError((e) => {
        console.log('e', e);
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
        return of(null as IInstallationDataResponse);
      }),
    );
  }

  public async loadDeviceInfo(
    gateway: string,
    project?: string,
  ): Promise<MqttGetDeviceDto | undefined> {
    return (
      await this.loadGatewayData<MqttGetDeviceDto[]>(
        gateway,
        'getDevice',
        'loadGatewayGetDevice',
        project,
      )
    )[0];
  }

  public async loadDeviceData(
    gateway: string,
    project?: string,
  ): Promise<IDeviceData | undefined> {
    const deviceData = (
      await this.loadGatewayData<IDeviceData[]>(
        gateway,
        'getData',
        'loadDeviceData',
        project,
      )
    )?.[0];
    return this.normalizeDeviceDataModuleAddresses(deviceData);
  }

  private normalizeDeviceDataModuleAddresses(
    deviceData?: IDeviceData,
  ): IDeviceData | undefined {
    deviceData?.uvsModule?.forEach((module) => {
      const parsedAddress = Number(module?.moduleInfo?.address);
      console.log('parsedAddress', parsedAddress);
      if (Number.isNaN(parsedAddress)) {
        const invalidAddress = module?.moduleInfo?.address;
        this.alertService.error(
          `Invalid module address "${invalidAddress}" received from backend.`,
        );
        throw new Error(
          `loadDeviceData - moduleInfo.address is not a number: ${invalidAddress}`,
        );
      }
      module.moduleInfo.address = parsedAddress;
    });

    return deviceData;
  }

  public async updateIrValues(
    gateway: string,
    project?: string,
  ): Promise<IUpdateIrValuesResponse | undefined> {
    return (
      await this.loadGatewayData<IUpdateIrValuesResponse[]>(
        gateway,
        'updateIrValues',
        'updateIrValues',
        project,
      )
    )[0];
  }

  public async getImplementedMeters(
    gateway: string,
    project?: string,
  ): Promise<IImplementedMetersResponse | undefined> {
    return (
      await this.loadGatewayData<IImplementedMetersResponse[]>(
        gateway,
        'getImplementedMeters',
        'getImplementedMeters',
        project,
      )
    )[0];
  }

  public async postSetConfig(
    gateway: string,
    modules: {
      type: GatewayType;
      address: number;
      irUartCfg?: { input: number; device: string }[];
      currentLoopThreshold?: number[];
      lightThreshold?: number;
      lekageThreshold?: number;
      temperatureThreshold?: number;
      sensorTypeName?: string;
      sensorParameters?: {
        b: number;
        referenceResistance: number;
      };
      measuredValues?: {
        sensorResistance: number;
        outsideTemperature: number;
        temperatureDisplayedByHeatingSystem: number;
        timestamp: string;
      };
    }[],
  ): Promise<IModuleConfig> {
    const message: {
      uvsModule: any[];
    } = {
      uvsModule: [],
    };
    modules.forEach((module) => {
      if (module.type === 'SM01' || module.type === 'SM04') {
        const moduleMessage = {
          typ: module.type,
          address: module.address,
          onChangeMsg: true,
          currentLoopDelta: module.currentLoopThreshold?.length ? module.currentLoopThreshold : [0.01, 0.01, 0.01, 0.01],
          lightThreshold: module.lightThreshold ?? 450,
          temperatureDelta: module.temperatureThreshold ?? 3.5,
          leakageDelta: [module.lekageThreshold ?? 50, module.lekageThreshold ?? 50],
        };
        message.uvsModule.push(moduleMessage);
      } else if (module.type === 'SM02' || module.type === 'SM03' || module.type === 'SM05') {
        const moduleMessage = {
          typ: module.type,
          address: module.address,
          onChangeMsg: true,
          currentLoopDelta: module.currentLoopThreshold?.length ? module.currentLoopThreshold : [0.01, 0.01, 0.01, 0.01],
          lightThreshold: module.lightThreshold ?? 450,
          temperatureDelta: module.temperatureThreshold ?? 3.5,
          leakageDelta: [module.lekageThreshold ?? 50, module.lekageThreshold ?? 50],
          irUartCfg: module.irUartCfg,
        };
        message.uvsModule.push(moduleMessage);
      } else if (module.type === 'OTM01' || module.type === 'OTM02' || module.type === 'OTM03') {
        const moduleMessage = {
          typ: module.type,
          address: module.address + this.OTM_ADDRESS_OFFSET,
          onChangeMsg: true,
          sensorTypeName: module.sensorTypeName ? module.sensorTypeName : null,
          sensorParameters: {
            b: module.sensorParameters?.b ? module.sensorParameters.b : null,
            referenceResistance: module.sensorParameters?.referenceResistance
              ? module.sensorParameters.referenceResistance
              : null,
          },
          measuredValues: {
            sensorResistance: module.measuredValues?.sensorResistance
              ? module.measuredValues.sensorResistance
              : null,
            outsideTemperature: module.measuredValues?.outsideTemperature
              ? module.measuredValues.outsideTemperature
              : null,
            temperatureDisplayedByHeatingSystem: module.measuredValues
              ?.temperatureDisplayedByHeatingSystem
              ? module.measuredValues.temperatureDisplayedByHeatingSystem
              : null,
            timestamp: module.measuredValues?.timestamp
              ? module.measuredValues.timestamp
              : null,
          },
        };
        message.uvsModule.push(moduleMessage);
      }
    });

    if (message === null || message.uvsModule?.length <= 0) {
      return null;
    }

    return (
      await this.loadGatewayData<IModuleConfig[]>(
        gateway,
        'setConfig',
        'postSetConfig',
        message,
      )
    )[0];
  }

  private loadGatewayData$<T>(
    gateway: string,
    method: string,
    methodName: string,
    message: any = null,
    project?: string,
  ): Observable<T> {
    if (!gateway) {
      return throwError(new Error(`${methodName} - gateway is not defined`));
    }
    if (this.mqttBlockingService.hasBlockedGateway(gateway)) {
      return throwError(
        new Error(`${methodName} - mqtt currently blocked for this gateway`),
      );
    }

    this.mqttBlockingService.addBlockedGateway(gateway);

    const expectedResponses = 1;
    const requestBody = {
      expectedResponses,
      mqtt_message: {
        id: this.uniqueIdService.getUniqueId(),
        methode: method,
        message,
      },
    };

    project = project || this.PROJECT_NAME;
    const url = `${this.dataBaseUrl}/api/v2/gatewayData/uvs/${project}/${gateway}`;

    return from(this.prepareAuthorizationBearerToken()).pipe(
      switchMap((authToken) =>
        this.http
          .post<T>(url, requestBody, {
            headers: {Authorization: authToken},
          })
          .pipe(timeout(90000 * expectedResponses)),
      ),
      finalize(() => this.mqttBlockingService.removeBlockedGateway(gateway)),
      catchError((e) => {
        if (e.status === 500) {
          this.alertService.error(
            'Internal Server Error, bitte versuchen Sie es später erneut.',
          );
        }
        return of(null as T);
      }),
    );
  }

  private async loadGatewayData<T>(
    gateway: string,
    method: string,
    methodName: string,
    message: any = null,
    project?: string,
  ): Promise<T | undefined> {
    return this.loadGatewayData$<T>(
      gateway,
      method,
      methodName,
      message,
      project,
    ).toPromise();
  }

  // https://juconn.atlassian.net/wiki/spaces/GIB/pages/2145845249/POST+new+Immoconn+Installation+v2
  public async postData(
    submissionData: InstallationSubmissionData,
    project?: string,
  ): Promise<any> {
    if (!project) {
      project = this.PROJECT_NAME;
    }

    console.log('submissionData', submissionData);
    const request =
      await this.prepareCreateInstallationDataRequestModel(submissionData);

    console.log('request', request);

    // https://juconn.atlassian.net/wiki/spaces/GIB/pages/2145845249/POST+new+Immoconn+Installation+v2
    const url = `${this.dataBaseUrl}/api/v3/${project}/installation`;

    try {
      await this.http
        .post<IDataGWResponse>(url, request, {
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            // "Method-Name": "DataService.postData",
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      this.alertService.error(
        'Internal Server Error, bitte versuchen Sie es später erneut.',
      );
      throw e;
    }
  }

  // The server rejects an installation with 400 and a list of field errors. Those are permanent -
  // resending the exact same payload cannot help - so the technician has to see the real reason
  // instead of the generic "try again later".
  public describePostDataError(error: unknown): string {
    const fieldErrors = DataService.getFieldErrors(error);
    if (fieldErrors.length === 0) {
      return 'Internal Server Error, bitte versuchen Sie es später erneut.';
    }
    return (
      'Die Montage-Daten wurden vom Server abgelehnt. Erneutes Senden hilft nicht, ' +
      'bitte den Support kontaktieren. Grund: ' +
      fieldErrors.map((fieldError) => fieldError.message).join(' | ')
    );
  }

  private static getFieldErrors(
    error: unknown,
  ): { field?: string; message?: string }[] {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return [];
    }
    return error.error?.fieldErrors ?? [];
  }

  public async getOtmVerificationData(
    deviceId: string,
    project?: string,
  ): Promise<OtmVerificationResponse | null> {
    const url = `${this.dataBaseUrl}/api/v2/gatewayData/uvs/${project ?? this.PROJECT_NAME}/${deviceId}`;
    const body = {
      expectedResponses: 1,
      mqtt_message: {
        id: this.uniqueIdService.getUniqueId(),
        methode: 'getAtmVerificationData',
        message: null,
      },
    };
    try {
      const result = await this.http
        .post<OtmVerificationResponse[]>(url, body, {
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
      return result?.[0] ?? null;
    } catch (e) {
      this.alertService.error(
        'Internal Server Error, bitte versuchen Sie es später erneut.',
      );
      throw e;
    }
  }

  public async postOngoingData(
    submissionData: InstallationSubmissionData,
    getDevice: MqttGetDeviceDto,
    deviceData: IDeviceData,
    project?: string,
  ): Promise<any> {
    if (!project) {
      project = this.PROJECT_NAME;
    }

    const request = this.prepareOngoingInstallationDataRequestModel(
      submissionData,
      getDevice,
      deviceData,
    );

    const url = `${this.dataBaseUrl}/api/v3/${project}/installation/ongoing`;

    try {
      return this.http
        .post<IDataGWResponse>(url, request, {
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            // "Method-Name": "DataService.postData",
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      this.alertService.error(this.describePostDataError(e));
      throw e;
    }
  }

  public async getGatewayBySerialNumber(
    serialNumber: string,
    project?: string,
  ): Promise<any> {
    return this.getGatewayBySerialNumber$(serialNumber, project).toPromise();
  }

  public login(login: {
    email: string;
    password: string;
    rememberMe: boolean;
  }) {
    const url = `${this.dataBaseUrl}/api/authenticate`;
    try {
      return this.http.post(url, login).toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public forgotPassword(email: string, resetUrl: string): Promise<void> {
    return this.http
      .post<void>(`${this.juconnApiUrl}/user/forgot_password`, {
        email,
        reset_url: resetUrl,
      })
      .toPromise();
  }

  public resetPassword(password: string, token: string): Promise<any> {
    return this.http
      .put(`${this.juconnApiUrl}/user/reset_password`, {
        password,
        token,
      })
      .toPromise();
  }

  public async getUser(userId): Promise<any> {
    return this.http
      .get(`${this.juconnApiUrl}/user/${userId}`, {
        headers: {
          Authorization: await this.prepareAuthorizationBearerToken(),
        },
      })
      .toPromise();
  }

  public async updateUser(user: User): Promise<any> {
    return this.http
      .put(`${this.juconnApiUrl}/user/${user._id}`, user, {
        headers: {
          Authorization: await this.prepareAuthorizationBearerToken(),
        },
      })
      .toPromise();
  }

  public async getCustomersWithAvailableSites() {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/availableSites`;
      return this.http
        .get(url, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async getFilteredPlannedSites(filter: PlannedSiteFilterModel) {
    const filterCopy = {...filter};
    if (filterCopy.jucCustomerId === -1) {
      filterCopy.jucCustomerId = undefined;
    }
    if (filterCopy.installationPersonEmail === 'null') {
      filterCopy.installationPersonEmail = undefined;
    }
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/filteredPlannedSites`;
      return this.http
        .post(url, filterCopy, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async createPlannedSite(site: SiteDto) {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/site`;
      return this.http
        .post(url, site, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async getAllCustomers() {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers`;
      return this.http
        .get(url, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async getAllServiceTechnicians() {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/serviceTechnicians`;
      return this.http
        .get(url, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async updatePlannedSite(site: SiteDto) {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/site`;
      return this.http
        .put(url, site, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public async updatePlannedSites(sites: SiteDto[]) {
    try {
      const url = `${this.dataBaseUrl}/api/v2/customers/site/list`;
      return this.http
        .put(url, sites, {
          headers: {
            Authorization: await this.prepareAuthorizationBearerToken(),
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  public refreshToken(refreshToken: string) {
    try {
      const url = `${this.dataBaseUrl}/api/token/refresh`;

      return this.http
        .get(url, {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        })
        .toPromise();
    } catch (e) {
      if (e.status === 500) {
        this.alertService.error(
          'Internal Server Error, bitte versuchen Sie es später erneut.',
        );
      }
    }
  }

  private async prepareAuthorizationBearerToken() {
    const user = this.userService.user;
    if (this.isTokenExpired(user.id_token) && user.id_refresh_token) {
      const refresh = (await this.refreshToken(
        this.userService.user.id_refresh_token,
      )) as {
        id_token: string;
        id_refresh_token: string;
      };
      user.id_token = refresh.id_token;
      user.id_refresh_token = refresh.id_refresh_token;
      this.userService.setUser(user);
    }
    return `Bearer ${this.userService.user.id_token}`;
  }

  private getTokenExpirationDate(token: string): Date {
    const decoded = jwt_decode.jwtDecode(token);
    if (decoded.exp === undefined) return null;
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  private isTokenExpired(token?: string): boolean {
    if (!token) return true;
    const date = this.getTokenExpirationDate(token);
    if (date === null) return false;
    return !(date.valueOf() > new Date().valueOf());
  }

  private cleanBase64(data: string): string {
    return data?.split(',')?.[1];
  }

  private stripImageArrays(array: { image: string }[]): string[] {
    if (array != null && array.length > 0) {
      const output: string[] = [];
      array.forEach((item) => {
        if (item?.image) {
          output.push(item.image);
        }
      });
      return output;
    }
    return undefined;
  }

  private async prepareCreateInstallationDataRequestModel(
    data: InstallationSubmissionData,
  ): Promise<CreateInstallationDataRequestModel> {
    const {
      building,
      generalGateway,
      generalMeasurement,
      generalPrePhotos,
      generalSending,
      heatingCheck,
      heatingInformation,
      photos,
      sensorsCounter,
      sensorsOther,
      sensorsOutsideTemp,
      sensorsPump,
      sensorsTemperature,
      gatewayId,
      simIp,
      technicianEmail,
      installationAt,
      signatureDate,
      signImage,
      signature,
      installationInSeveralRooms,
      commissioningMaterial,
    } = data;

    // ── Merge uvsModules: gateway modules + OTM modules from sensorsOutsideTemp ──
    const mergedUvsModules: UvsModuleModel[] = [
      ...(generalGateway?.uvsModules || []).filter(m => !m.markedForRemoval && !isReadOnlyModuleType(m.uvsType)),
    ];
    sensorsOutsideTemp?.otmModules?.forEach((otmModule) => {
      const index = mergedUvsModules.findIndex(
        (m) => m.modulId === otmModule.modulId,
      );
      if (index > -1) {
        mergedUvsModules[index] = otmModule;
      }
    });

    // ── Build flat sensor arrays from data-models ────────────────────────────
    const temperatureSensors: TemperatureSensorModel[] = [];
    sensorsTemperature?.temperatureSensorsHelperList?.forEach((sensor) => {
      temperatureSensors.push({
        sensorId: sensor.sensorId,
        name: sensor.name,
        value: sensor.value,
        image: sensor.photo,
        customName: sensor.customName,
      });
      if (sensor.pairMode !== SensorModeType.SINGLE) {
        temperatureSensors.push({
          sensorId: sensor.sensor2Id,
          name: sensor.name2,
          value: sensor.value2,
          image: sensor.photo2,
          customName: sensor.customName,
        });
      }
      if (sensor.pairMode === SensorModeType.TRIO) {
        temperatureSensors.push({
          sensorId: sensor.sensor3Id,
          name: sensor.name3,
          value: sensor.value3,
          image: sensor.photo3,
          customName: sensor.customName,
        });
      }
    });

    const pumpSensors = sensorsPump?.pumpSensors || [];
    const meterSensors = sensorsCounter?.counterSensors || [];
    const otherSensors = sensorsOther?.otherSensors || [];
    const pressureSensors = sensorsOther?.pressureSensors || [];
    const pfcSensors = sensorsOther?.pfcSensors || [];

    // ── Build CreateUvsModuleRequestModel + outsideTemperatureModules ─────────
    const uvsModules: CreateUvsModuleRequestModel[] = [];
    const outsideTemperatureModules: CreateOutsideTemperatureModuleRequestModel[] =
      [];

    if (mergedUvsModules.length > 0) {
      mergedUvsModules
        .filter((item) => item.uvsType !== GatewayType.OTM01 && item.uvsType !== GatewayType.OTM02 && item.uvsType !== GatewayType.OTM03)
        .forEach((module) => {
          uvsModules.push({
            modulId: module.modulId,
            uvsType: module.uvsType,
            address: module.address,
            meterSensors: [],
          });
        });
      mergedUvsModules
        .filter((item) => item.uvsType === GatewayType.OTM01 || item.uvsType === GatewayType.OTM02 || item.uvsType === GatewayType.OTM03)
        .forEach((module) => {
          outsideTemperatureModules.push({
            modulId: module.modulId,
            uvsType: module.uvsType,
            address: module.address + this.OTM_ADDRESS_OFFSET,
            sensorType: module.sensorTypeName,
            b: module.sensorParameters?.b,
            referenceResistance: module.sensorParameters?.referenceResistance,
            sensorResistance: module.measuredValues?.sensorResistance,
            outsideTemperature: module.measuredValues?.outsideTemperature,
            temperatureDisplayedByHeatingSystem:
            module.measuredValues?.temperatureDisplayedByHeatingSystem,
            timestamp: module.measuredValues?.timestamp,
          });
        });
    }

    // ── Assign temperature sensors to modules ─────────────────────────────────
    if (temperatureSensors.length > 0) {
      for (const sensor of temperatureSensors) {
        const key = sensor.sensorId.split('.')[0];
        const module = uvsModules.find((m) => m.address + '' === key);
        if (!module) {
          this.alertService.error(
            'Uvs Module for temperature sensor with key ' +
            sensor.sensorId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }
        if (!module.temperatureSensors) {
          module.temperatureSensors = [];
        }
        module.temperatureSensors.push({
          sensorId: sensor.sensorId,
          name: sensor.name,
          value: sensor.value,
          image: sensor.image,
          customName: sensor.customName,
        });
      }
    }

    // ── Assign pump sensors to modules ────────────────────────────────────────
    if (pumpSensors.length > 0) {
      for (const sensor of pumpSensors) {
        const key = sensor.sensorId.split('.')[0];
        const module = uvsModules.find((m) => m.address + '' === key);
        if (!module) {
          this.alertService.error(
            'Uvs Module for pump sensor with key ' +
            sensor.sensorId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }
        if (!module.pumpSensors) {
          module.pumpSensors = [];
        }
        module.pumpSensors.push({
          sensorId: sensor.sensorId,
          name: sensor.name,
          minValue: sensor.minValue,
          sensorType: sensor.sensorType,
          value: sensor.value,
          image: sensor.image,
          pumpBrand: sensor.pumpBrand,
          pumpType: sensor.pumpType,
          pumpElectronicallyControlled: sensor.pumpElectronicallyControlled,
          customName: sensor.customName,
        });
      }
    }

    // ── Assign meter sensors to modules ───────────────────────────────────────
    if (meterSensors.length > 0) {
      for (const sensor of meterSensors) {
        const key = sensor.module;
        const module = uvsModules.find((m) => m.address === key);
        if (!module) {
          this.alertService.error(
            'Uvs Module for meter sensor with key ' +
            sensor.sensorId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }

        let impulse: CreateMeterSensorsImpulseRequestModel = undefined;
        if (
          sensor.impulse !== undefined &&
          sensor.impulse !== null &&
          sensor.impulse.stepInUnit !== null &&
          sensor.impulse.initOffset
        ) {
          impulse = {
            key: sensor.module + '.' + sensor.sensorId,
            stepInUnit: sensor.impulse.stepInUnit,
            initOffset: sensor.impulse.initOffset,
          };
        }

        module.meterSensors.push({
          name: sensor.name,
          seriesNumber: sensor.seriesNumber,
          manufacturer: sensor.manufacturer,
          meterType: sensor.meterType,
          irType:
            sensor.irType === IRMeterTypeEnum.ALE3D5F
              ? IRMeterTypeEnum.SML
              : sensor.irType,
          value: sensor.value,
          impulse,
          image: sensor.image,
          customName: sensor.customName,
        });
      }
    }

    // ── Assign other sensors to modules + collect sensor images ──────────────
    const sensorImages: string[] = [];
    if (otherSensors.length > 0) {
      for (const sensor of otherSensors) {
        const key = sensor.uvsModuleId;
        const module = uvsModules.find((m) => m.address === key);
        if (!module) {
          this.alertService.error(
            'Uvs Module for other sensor with key ' +
            sensor.uvsModuleId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }

        sensorImages.push(sensor.image);
        if (
          module?.immoconnData === undefined ||
          module?.immoconnData === null
        ) {
          module.immoconnData = {};
        }
        switch (sensor.sensorType) {
          case OtherSensorOptionEnum.LEAKAGE_1:
            module.immoconnData.leakage1 = true;
            break;
          case OtherSensorOptionEnum.LEAKAGE_2:
            module.immoconnData.leakage2 = true;
            break;
          case OtherSensorOptionEnum.LIGHT:
            module.immoconnData.light = true;
            break;
          case OtherSensorOptionEnum.SHAFT_CONTACT_1:
            module.immoconnData.shaftContact1 = true;
            module.immoconnData.shaftContact1On = sensor.sensorOn;
            break;
          case OtherSensorOptionEnum.SHAFT_CONTACT_2:
            module.immoconnData.shaftContact2 = true;
            module.immoconnData.shaftContact2On = sensor.sensorOn;
            break;
          case OtherSensorOptionEnum.SWITCHABLE_OUTPUT_1:
            module.immoconnData.switchableOutput1 = true;
            break;
          case OtherSensorOptionEnum.SWITCHABLE_OUTPUT_2:
            module.immoconnData.switchableOutput2 = true;
            break;
        }
      }
    }

    // ── Assign pressure sensors to modules ────────────────────────────────────
    if (pressureSensors.length > 0) {
      for (const sensor of pressureSensors) {
        const key = sensor.sensorId.split('.')[0];
        const module = uvsModules.find((m) => m.address + '' === key);
        if (!module) {
          this.alertService.error(
            'Uvs Module for pressure sensor with key ' +
            sensor.sensorId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }
        if (!module.pressureSensor) {
          module.pressureSensor = {
            sensorId: sensor.sensorId,
            lowerLimit: sensor.lowerLimit,
            upperLimit: sensor.upperLimit,
            value: sensor.value,
            image: sensor.image,
          };
        }
      }
    }

    // ── Assign PFC sensors to modules ─────────────────────────────────────────
    if (pfcSensors.length > 0) {
      for (const sensor of pfcSensors) {
        const module = uvsModules.find((m) => m.address === sensor.uvsModuleId);
        if (!module) {
          this.alertService.error(
            'Uvs Module for PFC sensor with key ' +
            sensor.uvsModuleId +
            ' not found in uvsModules! Available modules address:' +
            uvsModules?.map((m) => m.address).join(', '),
          );
        }
        if (!module.pfcSensors) {
          module.pfcSensors = [];
        }
        module.pfcSensors.push({
          sensorId: sensor.sensorId,
          name: sensor.name,
          opened: !sensor.closed,
          image: sensor.image,
          customName: sensor.customName,
        });
      }
    }

    const productSpecificData: CreateProductSpecificDataRequestModel = {
      version: '3.0',
      generation: '1-serie',
      simIp: simIp,
      customerId: generalGateway?.selectedCustomer?.customerId,
      siteId: generalGateway?.selectedSite?.id,
      heatings: heatingInformation?.withHeating
        ? heatingInformation?.heatings?.map((heating) => ({
          heatingType: heating.heatingType,
          constructionYear: !isNaN(Number.parseInt(heating.constructionYear))
            ? Number.parseInt(heating.constructionYear)
            : undefined,
          manufacturer: heating.manufacturer,
          heatingTypeImage: heating.heatingTypeImage,
          heatingSystemImage: heating.heatingSystemImage,
          serialNumber: heating.serialNumber,
          heatingPower: heating.heatingPower,
        }))
        : null,
      heatingChecks: {
        averageRoomTemperature: heatingCheck?.averageRoomTemperature,
        emergencyStopAvailable: heatingCheck?.emergencyStopAvailable,
        insulatedPipes: heatingCheck?.insulatedPipes,
        insulatingCaps: heatingCheck?.insulatingCaps,
        outdoorTemperatureAvailable: heatingCheck?.outdoorTemperatureAvailable,
        summerWinterOperationAvailable:
        heatingCheck?.summerWinterOperationAvailable,
      },
      city: generalGateway?.selectedSite?.city,
      postCode: generalGateway?.selectedSite?.postCode,
      street: generalGateway?.selectedSite?.street,
      streetNumber: generalGateway?.selectedSite?.streetNumber,
      heatingLocation: building?.heatingLocation,
      heatingControlImage: heatingInformation?.heatingControlImage,
      heatingCollectorImage: photos?.heatingCollectorImage,
      heatingRoomLength: heatingInformation?.heatingRoomLength,
      heatingRoomWidth: heatingInformation?.heatingRoomWidth,
      heatingShutdownPossible: heatingInformation?.heatingShutdownPossible,
      heatingSpaceAvailable: heatingInformation?.heatingSpaceAvailable,
      schemeImage: photos?.schemeImage,
      heatingControlType: heatingInformation?.heatingControlType,
      contactPersonName: building?.contactPersonName,
      contactPersonNumber: building?.contactPersonNumber,
      installationBoxImage: photos?.installationBoxImage,
      antennaImage: photos?.antennaImage,
      mainMeterReading: building?.mainMeterReading
        ? building.mainMeterReading +
        (building.mainMeterReadingUnit
          ? ' ' + building.mainMeterReadingUnit
          : '')
        : undefined,
      addressComment: generalGateway?.addressComment,
      locationComment: generalGateway?.locationComment,
      comment: generalSending?.generalComment,
      installationComment: generalSending?.installationComment,
      nonInstallableSensors: generalSending?.nonInstallableSensors,
      hasDirectRemoteControl: sensorsOutsideTemp?.directControl?.enabled,
      controlType: sensorsOutsideTemp?.directControl?.type,
      uvsModules,
      outsideTemperatureModules,
      materials: [
        commissioningMaterial,
        generalMeasurement?.installationMaterial,
      ],
      sensorImages,
      preInstallBoilerRoomImages: this.stripImageArrays(
        generalPrePhotos?.preInstallBoilerRoomImages,
      ),
      preInstallHeatingImages: this.stripImageArrays(
        generalPrePhotos?.preInstallHeatingImages,
      ),
      postInstallBoilerRoomImages: this.stripImageArrays(
        photos?.postInstallBoilerRoomImages,
      ),
      postInstallHeatingImages: this.stripImageArrays(
        photos?.postInstallHeatingImages,
      ),
    };

    return {
      relatedEntity: 'GATEWAY',
      serialNo: gatewayId,
      productName: 'IMMOCONN',
      gatewayStyle: GatewayStyleEnum.JUCONN,
      technicianEmail: technicianEmail || this.userService.user.email,
      additionalGateway: generalGateway?.additionalGateway,
      installationInSeveralRooms: !!installationInSeveralRooms,
      installationAt: installationAt,
      signatureDate: signatureDate,
      signImage: this.cleanBase64(signImage),
      signingPerson: signature,
      productSpecificData,
    };
  }

  private prepareOngoingInstallationDataRequestModel(
    data: InstallationSubmissionData,
    getDevice: MqttGetDeviceDto,
    deviceData: IDeviceData,
  ): OngoingInstallationDataRequest {
    const {
      generalGateway,
      generalMeasurement,
      generalPrePhotos,
      generalSending,
      sensorsOutsideTemp,
      commissioningMaterial,
    } = data;

    // ── Merge uvsModules ──────────────────────────────────────────────────────
    const mergedUvsModules: UvsModuleModel[] = [
      ...(generalGateway?.uvsModules || []).filter(m => !m.markedForRemoval && !isReadOnlyModuleType(m.uvsType)),
    ];
    sensorsOutsideTemp?.otmModules?.forEach((otmModule) => {
      const index = mergedUvsModules.findIndex(
        (m) => m.modulId === otmModule.modulId,
      );
      if (index > -1) {
        mergedUvsModules[index] = otmModule;
      }
    });

    const uvsModules: OngoingUvsModuleRequest[] = [];
    const outsideTemperatureModules: OngoingOutsideTemperatureModuleRequest[] =
      [];
    if (mergedUvsModules.length > 0) {
      mergedUvsModules
        .filter((item) => item.uvsType !== GatewayType.OTM01 && item.uvsType !== GatewayType.OTM02 && item.uvsType !== GatewayType.OTM03)
        .forEach((module) => {
          uvsModules.push({
            modulId: module.modulId,
            uvsType: module.uvsType,
            address: module.address,
          });
        });
      mergedUvsModules
        .filter((item) => item.uvsType === GatewayType.OTM01 || item.uvsType === GatewayType.OTM02 || item.uvsType === GatewayType.OTM03)
        .forEach((module) => {
          outsideTemperatureModules.push({
            modulId: module.modulId,
            uvsType: module.uvsType,
            address: module.address + this.OTM_ADDRESS_OFFSET,
          });
        });
    }

    const productSpecificData: OngoingProductSpecificDataRequest = {
      version: '3.0',
      generation: '1-serie',
      simIp: getDevice.readInfo.gatewayInfo.ip,
      signalStrength: getDevice
        ? Number.parseFloat(getDevice.readInfo.gatewayInfo.snr)
        : 0,
      customerId: generalGateway?.selectedCustomer?.customerId,
      siteId: generalGateway?.selectedSite?.id,
      city: generalGateway?.selectedSite?.city,
      postCode: generalGateway?.selectedSite?.postCode,
      street: generalGateway?.selectedSite?.street,
      streetNumber: generalGateway?.selectedSite?.streetNumber,
      additionalName: null,
      uvsModules,
      outsideTemperatureModules,
      preInstallHeatingImages: this.stripImageArrays(
        generalPrePhotos?.preInstallHeatingImages,
      ),
      preInstallBoilerRoomImages: this.stripImageArrays(
        generalPrePhotos?.preInstallBoilerRoomImages,
      ),
      addressComment: generalGateway?.addressComment,
      locationComment: generalGateway?.locationComment,
      comment: generalSending?.generalComment,
      installationComment: generalSending?.installationComment,
      nonInstallableSensors: generalSending?.nonInstallableSensors,
      materials: [generalMeasurement?.installationMaterial],
    };

    return {
      relatedEntity: 'GATEWAY',
      serialNo: getDevice.readInfo?.configInfo?.locationID,
      productName: 'IMMOCONN',
      gatewayStyle: GatewayStyleEnum.JUCONN,
      technicianEmail: data.technicianEmail || this.userService.user.email,
      installationInSeveralRooms: !!data.installationInSeveralRooms,
      installationAt: new Date().toISOString(),
      productSpecificData,
      rawData: deviceData,
    };
  }
}
